<template>
  <div v-if="routine.id" class="w-100 align-self-start">
    <v-card flat outlined>
      <div
        class="d-flex align-center justify-space-between pt-4 pb-0 pl-6 pr-2 mb-2"
      >
        <h3 class="font-weight-medium">
          {{ $t('routines.finding_other') }}
        </h3>

        <Button
          v-if="findings.length"
          plain
          color="primary"
          :text="$t('common.more_details')"
          @click="$emit('goToFindings')"
        />
      </div>

      <v-data-table
        data-private
        item-key="id"
        class="row-pointer"
        hide-default-header
        :headers="headers"
        :items="findings"
        :options.sync="localOptions"
        :server-items-length="tableOptions.totalRecordsInData"
        :loading="isLoading"
        :footer-props="{
          itemsPerPageOptions: tableOptions.itemsPerPageOptions,
        }"
        :no-data-text="$t('routines.findings_empty')"
        :hide-default-footer="!findings.length"
        @click:row="handleAddressClick"
      >
        <template v-slot:item.status="{ item }">
          <v-badge :color="getColor(item.status)" />
        </template>
        <template v-slot:item.situation="{ item }">
          <v-tooltip top max-width="300">
            <template v-slot:activator="{ on }">
              <p
                v-on="on"
                class="ma-0 pa-0 text-body-2 text-truncate"
                style="max-width: 300px"
              >
                {{ item.situation }}
              </p>
            </template>
            {{ item.situation }}
          </v-tooltip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <Button
              color="blueGrey"
              data-testid="findingQuickView-button-editFinding"
              icon="mdi-pencil"
              :disabled="!canEditFinding({ routine, routineDefinition, finding: item })"
              @click.stop="handleEditClick(item.id)"
            />

            <Button
              color="blueGrey"
              data-testid="findingQuickView-button-deleteFinding"
              icon="mdi-trash-can-outline"
              :disabled="!canDeleteFinding({ routine, routineDefinition, finding: item })"
              @click.stop="handleDeleteClick(item)"
            />
          </div>
        </template>
      </v-data-table>

      <div
        v-if="canCreateFinding({ routine, routineDefinition })"
        data-testid="findingQuickView-container-findingComposer"
        class="d-flex flex-column pa-4"
      >
        <FindingComposer
          :routineDefinition="routineDefinition"
          :routine="routine"
          :selectedTagIds="selectedTagIds"
          @created="handleFindingCreated"
          @toggle-tag="handleToggleTag"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import mixin from '@/mixins/mixin'
import userMixin from '@/mixins/userMixin.vue'
import revMixin from '@/mixins/revMixin.vue'
import findingPermissions from '@/mixins/findingPermissions.vue'

import FindingComposer from '@/containers/RoutineExecutionView/FindingComposer.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'FindingsQuickView',

  mixins: [mixin, userMixin, revMixin, findingPermissions],

  components: {
    FindingComposer,
    Button,
  },

  props: {
    routine: {
      type: Object,
      required: true,
    },
    routineDefinition: {
      type: Object,
      required: true,
    },
    isLoading: Boolean,
    findings: {
      type: Array,
      required: true,
    },
    tableOptions: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      headers: [
        {
          width: 1,
          align: 'center',
          sortable: false,
          value: 'status',
        },
        {
          align: 'start',
          sortable: false,
          value: 'situation',
          width: '200px',
        },
        {
          align: 'end',
          sortable: false,
          value: 'actions',
          width: '130px',
        },
      ],

      selectedTagIds: [],
      situation: '',
    }
  },

  mounted() {
    if (this.$route?.params?.findingId) {
      this.selectedFindingId = this.$route?.params?.findingId
      this.$emit('openAddressFindingDrawer', this.$route?.params?.findingId)
    }
  },

  computed: {
    localOptions: {
      get() {
        return this.tableOptions
      },
      set(event) {
        this.$emit('getFindings', {
          page: event.page,
          itemsPerPage: event.itemsPerPage,
        })
      },
    },
  },

  methods: {
    handleAddressClick(finding) {
      this.$emit('openAddressFindingDrawer', finding.id)
    },

    handleEditClick(findingId) {
      this.$emit('openEditFindingDrawer', findingId)
    },

    handleDeleteClick(findingId) {
      this.$emit('deleteClick', findingId)
    },
    handleFindingCreated() {
      this.selectedTagIds = []
      this.$emit('getFindings', { page: 1 })
    },
    handleToggleTag(tagId) {
      if (this.selectedTagIds.includes(tagId)) {
        this.selectedTagIds = this.selectedTagIds.filter(
          selectedTagId => selectedTagId !== tagId
        )
        return
      }

      this.selectedTagIds = [...this.selectedTagIds, tagId]
    },
    getColor(status) {
      switch (status) {
        case this.$constants.findings.status.open:
          return 'teal lighten-2'
        case this.$constants.findings.status.inProgress:
          return 'blue lighten-2'
        case this.$constants.findings.status.canceled:
          return 'blueGrey lighten-2'
        case this.$constants.findings.status.onHold:
          return 'yellow lighten-2'
        case this.$constants.findings.status.completed:
          return 'green lighten-2'
        default:
          return ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.row-pointer:hover {
  cursor: pointer;
}
</style>
